import { HttpRequest, HttpMethod } from "./http";

class AppCatalogProductProvider extends HttpRequest {
    list(params) {
        return this.request(HttpMethod.Get, "/api/v1/bo/catalog/products", params);
    }

    get(id) {
        return this.request(HttpMethod.Get, `/api/v1/bo/catalog/products/${id}`);
    }

    create(payload) {
        return this.request(HttpMethod.Post, "/api/v1/bo/catalog/products", payload);
    }

    update(id, payload) {
        return this.request(HttpMethod.Put, `/api/v1/bo/catalog/products/${id}`, payload);
    }

    remove(id) {
        return this.request(HttpMethod.Delete, `/api/v1/bo/catalog/products/${id}`);
    }

    // ---- seller UI revamp 2026-10-03 (spec §3 BE-1) ----
    //
    // PATCH /api/v1/bo/catalog/products/:id/app-enable  {app_enable: 0|1}
    // 200 {id, app_enable, sellable_qty, updated_at}
    //
    // A SEPARATE ENDPOINT and not update() above: PUT /products/:id is a FULL
    // REPLACE — it requires price > 0, soft-deletes every SKU missing from the
    // payload and replaces images and option groups. Sending it a body with only
    // `app_enable` to flip a switch would empty the product.
    setAppEnable(id, appEnable) {
        return this.request(HttpMethod.Patch, `/api/v1/bo/catalog/products/${id}/app-enable`, {
            app_enable: Number(appEnable) === 1 ? 1 : 0
        });
    }

    // ---- inline price / quantity edit 2026-10-03 (spec §2 BE-1) ----
    //
    // PATCH /api/v1/bo/catalog/products/:id/skus/:sku_id  {price?, quantity?}
    // 200 {id, sku_id, price, quantity, stock_source, sellable_qty, price_min,
    //      price_max, updated_at, notes?[]}
    // 409 {code:"quantity_wms_owned"|"price_mode_fixed"}, 400, 404.
    //
    // Same reason setAppEnable() exists rather than update(): PUT /products/:id
    // is a full replace and would soft-delete every SKU missing from the body.
    // At least one field must be present; an absent field means "unchanged", so
    // the caller sends only what the operator actually typed (skuEditPayload in
    // @/utils/sellerProductRow decides that).
    patchSku(id, skuId, payload) {
        return this.request(
            HttpMethod.Patch,
            `/api/v1/bo/catalog/products/${id}/skus/${skuId}`,
            payload || {}
        );
    }

    // ---- #301 shared stock: SKUs whose WMS mapping is not usable ----
    //
    // GET /api/v1/bo/catalog/stock/unmatched (spec §8, BE-3) — read-only, JWT as
    // every other catalog route. 200 {count, warnings:[{code:"wms_unmapped"|
    // "wms_unmatched", sku_code, message}]}; `page`/`limit` are passed through
    // for the paginated form. These SKUs keep their hand-typed quantity and are
    // never pushed from the warehouse (spec Q3/Q6), which is why they need a
    // screen of their own rather than a line in a log.
    stockUnmatched(params) {
        return this.request(HttpMethod.Get, "/api/v1/bo/catalog/stock/unmatched", params);
    }

    // Push storefront-authored products/SKUs into Backoffice as platform
    // MDFStore (9) so BO can resolve WMS on platform-9 order ingest (#339).
    // Manual trigger for the storefront -> BO direction; NOT the retired
    // BO -> App sweep. Contract (Dev-2 #339, verified against main.go:385 +
    // routeplatform.go:56): async job — 200 { job_id, status: "queued" } or
    // 422 { job_id, status, message } when a job is already in progress.
    syncToBackoffice() {
        return this.request(HttpMethod.Post, "/api/platform/v2/mdfstore/products/sync");
    }

    // ---- Phase 3: push a seller product to a platform (#263 / bo-api#587) ----

    // POST .../push/:platform_id  {dry_run, overrides}   (spec §6B.4)
    //
    // dry_run TRUE is a preview: it never writes on the platform and never
    // uploads an image, but it does read the attribute tree and the shop
    // setting, so it can report the real rejects. dry_run FALSE creates the job
    // and returns immediately with job_id — the work happens in the worker and
    // is followed with pushStatus() below.
    //
    // Errors the modal handles: 409 / 422-with-job_id (a push is already in
    // flight), 422 {rejects}, 400 {code:"category_not_mapped"},
    // 400 {code:"platform_not_supported"}, 503 {code:"amaze_*"}.
    push(id, platformId, payload) {
        return this.request(
            HttpMethod.Post,
            `/api/v1/bo/catalog/products/${id}/push/${platformId}`,
            payload || {}
        );
    }

    // GET .../push/:platform_id/status — polled every 2s while the link state
    // is `pushing`. Returns {state, platform_product_id, last_synced_at,
    // last_error, state_detail, job:{id,status,step,progress,total,result}}.
    pushStatus(id, platformId) {
        return this.request(HttpMethod.Get, `/api/v1/bo/catalog/products/${id}/push/${platformId}/status`);
    }
}

export default AppCatalogProductProvider;
